import React, { useState, useEffect } from 'react'
import {
	Modal,
	TextInput,
	Select,
	Button,
	Group,
	Text,
	ActionIcon,
	Box,
	Divider,
} from '@mantine/core'
import Pencil from 'tabler-icons-react/dist/icons/pencil'
import Trash from 'tabler-icons-react/dist/icons/trash'
import Plus from 'tabler-icons-react/dist/icons/plus'

const WarningsModal = ({ opened, onClose, onSave, initial }) => {
	const [warnings, setWarnings] = useState([])
	const [editingIndex, setEditingIndex] = useState(null)
	const [newTitle, setNewTitle] = useState('')
	const [newLevel, setNewLevel] = useState('Warning')
	const [addingNew, setAddingNew] = useState(false)

	useEffect(() => {
		setWarnings(initial ? [...initial] : [])
		setEditingIndex(null)
		setNewTitle('')
		setNewLevel('Warning')
		setAddingNew(false)
	}, [initial, opened])

	const handleEdit = (index) => {
		const warning = warnings[index]
		setNewTitle(warning.title)
		setNewLevel(warning.level)
		setEditingIndex(index)
		setAddingNew(true)
	}

	const handleDelete = (index) => {
		if (window.confirm('Are you sure you want to remove this warning?')) {
			const updated = [...warnings]
			updated.splice(index, 1)
			setWarnings(updated)
		}
	}

	const handleAddOrUpdate = () => {
		if (!newTitle || !newLevel) return
		const updated = [...warnings]
		const newWarning = { title: newTitle, level: newLevel }

		if (editingIndex !== null) {
			updated[editingIndex] = newWarning
		} else {
			updated.push(newWarning)
		}

		setWarnings(updated)
		setNewTitle('')
		setNewLevel('Warning')
		setEditingIndex(null)
		setAddingNew(false)
	}

	const handleModalDone = () => {
		onSave(warnings)
		onClose()
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title='Edit Warnings'
			centered
			size='lg'
		>
			{warnings.length === 0 && (
				<Text color='dimmed' fs='italic' mb='md'>
					No warnings added yet.
				</Text>
			)}

			{warnings.map((warning, index) => (
				<Group key={index} position='apart' mb='xs'>
					<Text>
						<strong>{warning.level}</strong>: {warning.title}
					</Text>
					<Group spacing={4}>
						<ActionIcon onClick={() => handleEdit(index)}>
							<Pencil size={16} />
						</ActionIcon>
						<ActionIcon color='red' onClick={() => handleDelete(index)}>
							<Trash size={16} />
						</ActionIcon>
					</Group>
				</Group>
			))}

			{addingNew ? (
				<Box mt='md'>
					<TextInput
						label='Title'
						value={newTitle}
						onChange={(e) => setNewTitle(e.target.value)}
						required
						mb='sm'
					/>
					<Select
						label='Level'
						value={newLevel}
						onChange={setNewLevel}
						data={['Warning', 'Critical']}
						required
						mb='sm'
					/>
					<Group position='right'>
						<Button
							variant='subtle'
							color='gray'
							size='xs'
							onClick={() => {
								setNewTitle('')
								setNewLevel('Warning')
								setEditingIndex(null)
								setAddingNew(false)
							}}
						>
							Cancel
						</Button>
						<Button size='xs' onClick={handleAddOrUpdate}>
							{editingIndex !== null ? 'Update' : 'Add'}
						</Button>
					</Group>
				</Box>
			) : (
				<Button
					variant='outline'
					leftIcon={<Plus size={16} />}
					size='xs'
					mt='md'
					onClick={() => setAddingNew(true)}
				>
					Add Warning
				</Button>
			)}

			<Divider my='md' />

			<Group position='right'>
				<Button onClick={handleModalDone}>Done</Button>
			</Group>
		</Modal>
	)
}

export default WarningsModal
